'use client'

import { Icon } from '@components/primitives/Icon'
import { classMerge } from '@components/utilities/classMerge'
import { faMapMarkerAlt } from '@fortawesome/pro-regular-svg-icons/faMapMarkerAlt'
import { useCallback, useEffect, useState } from 'react'
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService'

export const GoogleMapsAutocomplete = ({
  addressClearedCallback,
  className,
  name,
  required,
  setPlace,
}: {
  addressClearedCallback?: () => void
  className?: string
  name?: string
  required?: boolean
  setPlace: (place: google.maps.places.PlaceResult) => void
}) => {
  const [inputValue, setInputValue] = useState<string>('')
  const [autocompletePlaceId, setAutocompletePlaceId] = useState<string>()
  const [showResults, setShowResults] = useState<boolean>(false)

  const { getPlacePredictions, placePredictions, placesService } =
    usePlacesService({
      apiKey: process.env.NEXT_PUBLIC_GOOGLE_GEOCODE_API_KEY,
    })

  useEffect(() => {
    if (placePredictions.length) {
      placesService?.getDetails(
        {
          placeId: placePredictions[0]!.place_id,
        },
        (_placeDetails) => {},
      )
    }
  }, [placePredictions])

  const handleSelectItem = async (
    item: google.maps.places.AutocompletePrediction,
  ) => {
    setInputValue(item.description)
    placesService?.getDetails({ placeId: item.place_id }, (place) =>
      place ? setPlace(place) : null,
    )
    setAutocompletePlaceId(item.place_id)
    setShowResults(false)
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setInputValue(value)
    if (value) {
      getPlacePredictions({ input: value, types: ['address'] })
      setShowResults(true)
    } else {
      setShowResults(false)
    }
  }

  const handleBlur = useCallback(() => {
    if (!autocompletePlaceId) {
      setInputValue('')
      setTimeout(() => {
        setShowResults(false)
        addressClearedCallback && addressClearedCallback()
        // todo - magic number but necessary to allow the selection of the item to resolve
      }, 200)
    }
  }, [autocompletePlaceId])

  return (
    <div className='relative'>
      <input
        className={className}
        name={name}
        onBlur={handleBlur}
        onChange={handleInputChange}
        onFocus={() => inputValue && setShowResults(true)}
        placeholder='Enter a location'
        required={required}
        value={inputValue}
      />
      <ul
        className={classMerge(
          'flex flex-col gap-2 rounded-md border border-neutral200 p-2',
          showResults && placePredictions.length > 0
            ? 'absolute z-10 w-full bg-white'
            : 'hidden',
        )}
      >
        {placePredictions.map((item) => (
          <li
            className='cursor-pointer border-b border-neutral200 px-1 py-2 text-sm last:border-0  hover:bg-neutral100'
            key={item.place_id}
            onClick={() => handleSelectItem(item)}
          >
            <Icon className='mr-1' icon={faMapMarkerAlt} />
            {item.description}
          </li>
        ))}
      </ul>
    </div>
  )
}
